import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface FormState {
    name: string;
    label: string;
    form_type: string;
    form_fields: any[];
    lastUpdated?: number;
    [key: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class FormStateService {
    private readonly STORAGE_KEY = 'form_builder_state';
    private readonly UPDATE_CHANNEL = 'form_state_broadcast';
    private formState: BehaviorSubject<FormState>;
    private broadcastChannel: BroadcastChannel;

    constructor() {
        this.broadcastChannel = new BroadcastChannel(this.UPDATE_CHANNEL);
        
        const storedState = this.getStoredState();
        this.formState = new BehaviorSubject<FormState>(storedState);

        this.broadcastChannel.onmessage = (event) => {
            if (event.data.type === 'STATE_UPDATE') {
                sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(event.data.state));
                this.formState.next(event.data.state);
            }
        };
    }

    private getStoredState(): FormState {
        const stored = sessionStorage.getItem(this.STORAGE_KEY);
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                return this.initialState;
            }
        }
        return this.initialState;
    }

    private get initialState(): FormState {
        return {
            name: '',
            label: '',
            logo: '',
            cover_image: null,
            header_content: null,
            footer_content: null,
            form_type: '',
            form_fields: []
        };
    }

    getFormState(): Observable<FormState> {
        return this.formState.asObservable();
    }

    updateFormState(newState: Partial<FormState>) {
        if (!this.validateStateUpdate(newState)) {
            return;
        }

        const updatedState = {
            ...this.formState.value,
            ...newState,
            lastUpdated: new Date().getTime()
        };

        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(updatedState));
        
        this.broadcastChannel.postMessage({
            type: 'STATE_UPDATE',
            state: updatedState
        });

        this.formState.next(updatedState);
    }

    private validateStateUpdate(newState: Partial<FormState>): boolean {
        return true;
    }

    clearState() {
        const emptyState: FormState = {
            name: '',
            label: '',
            logo: '',
            cover_image: null,
            header_content: null,
            footer_content: null,
            form_type: '',
            form_fields: [],
            lastUpdated: new Date().getTime()
        };
        
        sessionStorage.removeItem(this.STORAGE_KEY);
        this.formState.next(emptyState);
        
        this.broadcastChannel.postMessage({
            type: 'STATE_CLEAR'
        });
    }

    resetToInitialState() {
        this.clearState();
        this.broadcastChannel.close();
        this.broadcastChannel = new BroadcastChannel(this.UPDATE_CHANNEL);
    }

    ngOnDestroy() {
        this.broadcastChannel.close();
    }
}